/**
 * Dimensional analysis over transfer functions (PRD §4.4).
 *
 * Catches the errors that produce a plausible-looking number: a length added to
 * a time, a resistance where a voltage belongs, an output declared in volts
 * whose expression evaluates to watts. PRD §8.5 raised this after two
 * mis-scaled models were built in a single session — one a load current off by
 * 100× — and both are in the class this catches.
 *
 * Two design rules, both about earning trust rather than maximising coverage:
 *
 * 1. **Never guess.** A unit the registry does not recognise makes the quantity
 *    *unknown*, and unknown propagates: every check involving it is skipped
 *    rather than assumed. Units are free text in a domain-agnostic tool, so a
 *    checker that fires on `pcs` or `LSB` would be turned off within a day and
 *    would then be protecting nothing.
 *
 * 2. **Magnitude counts, not just dimension.** The tool evaluates the numbers
 *    you typed and never converts, so `housing_mm - shaft_m` is arithmetically
 *    wrong even though both sides are lengths. Scale is tracked alongside
 *    dimension and mismatches are reported with the factor involved.
 *
 * Findings are advisory. They surface as warnings and never block a run —
 * a unit label is documentation, and being wrong about one does not make the
 * arithmetic the user asked for un-runnable.
 */

import type { Node } from '../expr/ast.ts';
import { CONSTANTS, formatExpr } from '../expr/ast.ts';
import { parse } from '../expr/parse.ts';
import type { Project } from '../model.ts';
import { dimEqual, dimMul, dimDiv, dimPow, isDimensionless, DIMENSIONLESS } from './dimension.ts';
import { describeDimension } from './registry.ts';
import { parseUnit } from './parse.ts';

export type IssueCode =
  | 'dimension-mismatch'
  | 'scale-mismatch'
  | 'declared-dimension'
  | 'declared-scale'
  | 'not-dimensionless'
  | 'variable-exponent';

export interface DimensionIssue {
  /** The intermediate or output whose expression contains the problem. */
  kind: 'intermediate' | 'output';
  id: string;
  code: IssueCode;
  message: string;
}

/**
 * A quantity's unit as the walker knows it.
 *
 * `poly` is the one that makes this usable on real models. A bare numeric
 * literal has no unit, but `T - 25` is not an error — the 25 is 25 °C, written
 * the way every engineer writes it. So a literal adopts whatever unit it is
 * added to, and only behaves as a dimensionless number under multiplication.
 * Without this the shipped divider example, which is correct, would be reported
 * as broken.
 */
type Quantity =
  | { t: 'known'; dimension: readonly number[]; scale: number }
  | { t: 'unknown' }
  | { t: 'poly' };

const UNKNOWN: Quantity = { t: 'unknown' };
const POLY: Quantity = { t: 'poly' };
const PURE: Quantity = { t: 'known', dimension: DIMENSIONLESS, scale: 1 };

function sameScale(a: number, b: number): boolean {
  if (a === b) return true;
  if (!Number.isFinite(a) || !Number.isFinite(b) || b === 0) return false;
  return Math.abs(a / b - 1) < 1e-9;
}

/** `1000×`, `0.001×` — readable at the magnitudes unit errors actually take. */
function formatFactor(ratio: number): string {
  const abs = Math.abs(ratio);
  if (abs >= 1e-4 && abs < 1e6) {
    return `${Number(ratio.toPrecision(6))}×`;
  }
  return `${ratio.toExponential(2)}×`;
}

/** Short enough to read inside a warning line. */
function snippet(node: Node): string {
  const text = formatExpr(node);
  return text.length > 64 ? `${text.slice(0, 61)}…` : text;
}

function describe(q: Quantity): string {
  if (q.t !== 'known') return 'an unknown unit';
  return describeDimension(q.dimension);
}

type Report = (code: IssueCode, message: string) => void;

/** Used when a quantity is being inferred rather than checked. */
const IGNORE: Report = () => {};

class Checker {
  constructor(
    private readonly env: ReadonlyMap<string, Quantity>,
    private readonly report: Report,
  ) {}

  /**
   * Combine two operands of `+`, `-`, `min`, `max` or a comparison, which all
   * require their arguments to be the same quantity.
   */
  private combine(op: string, node: Node, a: Quantity, b: Quantity): Quantity {
    if (a.t === 'unknown' || b.t === 'unknown') return UNKNOWN;
    if (a.t === 'poly') return b;
    if (b.t === 'poly') return a;

    if (!dimEqual(a.dimension, b.dimension)) {
      this.report(
        'dimension-mismatch',
        `${op} ${describe(a)} and ${describe(b)} in \`${snippet(node)}\` — these are ` +
          `different physical quantities.`,
      );
      return UNKNOWN;
    }

    if (!sameScale(a.scale, b.scale)) {
      this.report(
        'scale-mismatch',
        `${op} two quantities of the same kind measured in different units in ` +
          `\`${snippet(node)}\` — they differ by ${formatFactor(a.scale / b.scale)}. Values are ` +
          `used exactly as entered, so this arithmetic is wrong unless one side is rescaled.`,
      );
      return UNKNOWN;
    }

    return a;
  }

  private requireDimensionless(fn: string, node: Node, q: Quantity): Quantity {
    if (q.t === 'known' && !isDimensionless(q.dimension)) {
      this.report(
        'not-dimensionless',
        `${fn}() needs a dimensionless argument but was given ${describe(q)} in ` +
          `\`${snippet(node)}\`.`,
      );
    }
    return PURE;
  }

  walk(node: Node): Quantity {
    switch (node.kind) {
      case 'num':
        return POLY;

      case 'ref':
        if (node.name in CONSTANTS) return POLY;
        return this.env.get(node.name) ?? UNKNOWN;

      case 'neg':
        return this.walk(node.operand);

      case 'binary': {
        const left = this.walk(node.left);
        const right = this.walk(node.right);

        switch (node.op) {
          case '+':
            return this.combine('adds', node, left, right);
          case '-':
            return this.combine('subtracts', node, left, right);
          case '*':
            if (left.t === 'unknown' || right.t === 'unknown') return UNKNOWN;
            if (left.t === 'poly') return right;
            if (right.t === 'poly') return left;
            return {
              t: 'known',
              dimension: dimMul(left.dimension, right.dimension),
              scale: left.scale * right.scale,
            };
          case '/':
            if (left.t === 'unknown' || right.t === 'unknown') return UNKNOWN;
            if (right.t === 'poly') return left;
            if (left.t === 'poly') {
              return {
                t: 'known',
                dimension: dimDiv(DIMENSIONLESS, right.dimension),
                scale: 1 / right.scale,
              };
            }
            return {
              t: 'known',
              dimension: dimDiv(left.dimension, right.dimension),
              scale: left.scale / right.scale,
            };
          case '^': {
            const exponent = constantValue(node.right);
            if (exponent === null) {
              // A dimensionless base may be raised to anything; a dimensioned
              // one cannot, because the result's dimension would vary per trial.
              if (left.t === 'known' && !isDimensionless(left.dimension)) {
                this.report(
                  'variable-exponent',
                  `\`${snippet(node)}\` raises ${describe(left)} to a power that is not a ` +
                    `constant, so the result has no fixed dimension.`,
                );
              }
              return UNKNOWN;
            }
            if (left.t !== 'known') return left.t === 'poly' ? POLY : UNKNOWN;
            return {
              t: 'known',
              dimension: dimPow(left.dimension, exponent),
              scale: Math.pow(left.scale, exponent),
            };
          }
        }
        break;
      }

      case 'compare': {
        const left = this.walk(node.left);
        const right = this.walk(node.right);
        this.combine('compares', node, left, right);
        return PURE;
      }

      case 'if': {
        this.walk(node.cond);
        return this.combine(
          'chooses between',
          node,
          this.walk(node.then),
          this.walk(node.otherwise),
        );
      }

      case 'call': {
        const args = node.args.map((a) => this.walk(a));
        switch (node.name) {
          // Shape-preserving: the result is in the same unit as the input.
          case 'abs':
          case 'floor':
          case 'ceil':
          case 'round':
            return args[0];

          case 'sqrt':
            if (args[0].t !== 'known') return args[0];
            return {
              t: 'known',
              dimension: dimPow(args[0].dimension, 0.5),
              scale: Math.sqrt(args[0].scale),
            };

          case 'min':
          case 'max':
            return this.combine(`${node.name}() compares`, node, args[0], args[1]);

          case 'clamp': {
            const bounds = this.combine('clamp() compares', node, args[1], args[2]);
            return this.combine('clamp() compares', node, args[0], bounds);
          }

          case 'atan2':
            this.combine('atan2() compares', node, args[0], args[1]);
            return PURE;

          // Transcendental: the argument must be a pure number, and so is the
          // result. e^(5 volts) is not a quantity.
          default:
            return this.requireDimensionless(node.name, node, args[0]);
        }
      }
    }
    return UNKNOWN;
  }
}

/** The literal value of a node, when it is one. */
function constantValue(node: Node): number | null {
  if (node.kind === 'num') return node.value;
  if (node.kind === 'neg') {
    const inner = constantValue(node.operand);
    return inner === null ? null : -inner;
  }
  return null;
}

export interface UnitCoverage {
  /** Variables whose unit the registry resolved. */
  recognised: number;
  total: number;
  /** Ids of variables carrying no unit at all. */
  unlabelled: string[];
  /** Distinct unit strings that were set but not understood. */
  unrecognised: string[];
}

/**
 * How much of the model dimensional analysis can actually see.
 *
 * Without this the feature fails silently: a single unlabelled coefficient
 * makes every expression using it unknown, the checker reports nothing, and the
 * user reasonably concludes their model is clean. Reporting coverage turns that
 * silence into an actionable "add a unit to `k` and this starts working".
 */
export function unitCoverage(project: Project): UnitCoverage {
  const unlabelled: string[] = [];
  const unrecognised = new Set<string>();
  let recognised = 0;

  for (const v of project.variables) {
    const text = v.unit?.trim();
    if (!text) {
      unlabelled.push(v.id);
    } else if (parseUnit(text)) {
      recognised++;
    } else {
      unrecognised.add(text);
    }
  }

  return {
    recognised,
    total: project.variables.length,
    unlabelled,
    unrecognised: [...unrecognised],
  };
}

/**
 * Check every intermediate and output in a project.
 *
 * Safe to call on a project that does not build: unparseable expressions are
 * skipped, since a syntax error is already reported elsewhere and a second
 * complaint about its units would be noise.
 */
export function checkDimensions(project: Project): DimensionIssue[] {
  const asts = new Map<string, Node>();
  const parseOrSkip = (expr: string): Node | null => {
    try {
      return parse(expr);
    } catch {
      return null;
    }
  };

  const intermediates = project.intermediates ?? [];
  for (const m of intermediates) {
    const ast = parseOrSkip(m.expr);
    if (ast) asts.set(m.id, ast);
  }

  const env = new Map<string, Quantity>();
  for (const v of project.variables) {
    const unit = parseUnit(v.unit);
    env.set(v.id, unit ? { t: 'known', dimension: unit.dimension, scale: unit.scale } : UNKNOWN);
  }

  /*
   * An intermediate's quantity is its declared unit when it has one, and
   * otherwise whatever its expression works out to — so a model that only
   * labels its variables and its final output is still checked end to end.
   * `resolving` guards against a cycle; buildModel rejects those, but this may
   * be called on a project that has not been built yet.
   */
  const resolving = new Set<string>();
  const resolve = (id: string): Quantity => {
    const cached = env.get(id);
    if (cached) return cached;
    if (resolving.has(id)) return UNKNOWN;

    const declared = intermediates.find((m) => m.id === id);
    if (!declared) return UNKNOWN;

    const unit = parseUnit(declared.unit);
    if (unit) {
      const q: Quantity = { t: 'known', dimension: unit.dimension, scale: unit.scale };
      env.set(id, q);
      return q;
    }

    resolving.add(id);
    const ast = asts.get(id);
    // Inference only — any problem inside this intermediate is reported when
    // the intermediate is checked in its own right, not a second time here.
    const q = ast ? new Checker(lazyEnv, IGNORE).walk(ast) : UNKNOWN;
    resolving.delete(id);
    env.set(id, q);
    return q;
  };

  // A view that resolves intermediates on demand, so declaration order in the
  // file does not decide what gets checked.
  const lazyEnv: ReadonlyMap<string, Quantity> = {
    get: (key: string) => (env.has(key) ? env.get(key) : resolve(key)),
    has: (key: string) => env.has(key) || intermediates.some((m) => m.id === key),
  } as ReadonlyMap<string, Quantity>;

  const issues: DimensionIssue[] = [];

  const checkOne = (
    kind: 'intermediate' | 'output',
    id: string,
    ast: Node,
    declared: string | undefined,
  ): void => {
    const report: Report = (code, message) => issues.push({ kind, id, code, message });
    const got = new Checker(lazyEnv, report).walk(ast);

    const want = parseUnit(declared);
    if (!want || got.t !== 'known') return;

    if (!dimEqual(got.dimension, want.dimension)) {
      // Name the dimension only when it adds something: "declared in A (A)"
      // reads like a stutter, while "declared in kg*m/s^2 (N)" is a genuine hint.
      const text = declared!.trim();
      const canonical = describeDimension(want.dimension);
      const wanted = canonical === text ? text : `${text} (${canonical})`;
      report(
        'declared-dimension',
        `${id} is declared in ${wanted} but its expression evaluates to ` +
          `${describeDimension(got.dimension)}.`,
      );
    } else if (!sameScale(got.scale, want.scale)) {
      report(
        'declared-scale',
        `${id} is declared in ${declared!.trim()} but its expression evaluates ` +
          `${formatFactor(got.scale / want.scale)} larger. No conversion is applied — the number ` +
          `shown is whatever the expression produced.`,
      );
    }
  };

  for (const m of intermediates) {
    const ast = asts.get(m.id);
    if (ast) checkOne('intermediate', m.id, ast, m.unit);
  }
  for (const o of project.outputs) {
    const ast = parseOrSkip(o.expr);
    if (ast) checkOne('output', o.id, ast, o.unit);
  }

  return issues;
}
